import { Row } from 'Components/Grid';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { addPolicyVersion, editPolicyVersion, getPolicyVersionByPolicyIdAndVersion, updatePolicyVersionAttachment } from 'services/policies-service';
import { useNavigate, useParams } from 'react-router-dom';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VersionFormComponent } from './Components/Form';
import { Container } from 'Components/Container';
import { TitleForm } from 'Components/Form/styles';
import { FormSuccess } from 'Components/Form/Success';

export const PolicyVersionForm = () => {
  const { id, idVersion } = useParams();
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const [idPolicyVersion, setIdPolicyVersion] = useState(null);
  const [existingData, setExistingData] = useState(null);

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      approvedDate: null,
      approver: null,
      author: null,
      policyText: '',
      policyVersionCommentaries: [],
      publishedDate: null,
      reviewedDate: null,
      reviewer: null,
      status: null,
      version: null,
      attachment: null,
    },
  });

  // 🔑 chave de idempotência por clique (apenas para criação)
  const idemRef = useRef(null);

  // mutation aceita "variables" (payload normalizado vindo do filho)
  const mutation = useMutation(
    async (normalizedPayload) => {
      if (idVersion) {
        // Edição não usa upload (seu endpoint PUT não tem multer)
        return editPolicyVersion(id, idVersion, normalizedPayload);
      }
      // Criação: gera/reutiliza idempotencyKey para dedupe server-side
      if (!idemRef.current) {
        idemRef.current = (window.crypto?.randomUUID?.()) ?? `${Date.now()}-${Math.random()}`;
      }
      return addPolicyVersion(id, { ...normalizedPayload, idempotencyKey: idemRef.current });
    },
    {
      onError: (error) => {
        const SwalReact = withReactContent(Swal);
        SwalReact.fire({
          title: 'Erro!',
          html: error?.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: (res) => {
        // res.status 200 (PUT) ou 201 (POST)
        if (res?.status === 200) {
          setIdPolicyVersion(idVersion);
        } else if (res?.status === 201) {
          setIdPolicyVersion(res?.data?.policyVersionId);
        }
        setFinished(true);
      },
      onSettled: () => {
        idemRef.current = null; // libera a chave após concluir
      },
    }
  );

  // chamado pelo filho; recebe payload normalizado; quem posta é o pai
  const handleComplete = async (normalizedPayload) => {
    await mutation.mutateAsync(normalizedPayload);
  };

  // Nova função específica para atualizar apenas o anexo
  const attachmentMutation = useMutation(
    async (newFile) => {
      if (!idVersion) {
        throw new Error('Versão da política não encontrada para atualizar anexo');
      }
      return updatePolicyVersionAttachment(id, idVersion, newFile);
    },
    {
      onError: (error) => {
        const SwalReact = withReactContent(Swal);
        SwalReact.fire({
          title: 'Erro!',
          html: error?.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: () => {
        const SwalReact = withReactContent(Swal);
        SwalReact.fire({
          title: 'Sucesso!',
          text: 'Documento atualizado com sucesso!',
          icon: 'success',
        }).then(() => {
          // Recarregar a página para mostrar o novo documento
          window.location.reload();
        });
      },
    }
  );

  const handleUpdateAttachment = async (newFile) => {
    await attachmentMutation.mutateAsync(newFile);
  };

  const handleBack = () => {
    navigate(`/policies/show/${id}`);
  };

  const getTitleStep = () => {
    if (finished) return <></>;
    return (
      <Row>
        <TitleForm>Versão</TitleForm>
      </Row>
    );
  };

  const getPolicyVersion = useCallback(async () => {
    if (id && idVersion) {
      try {
        const resPolicy = await getPolicyVersionByPolicyIdAndVersion(id, idVersion);
        const data = resPolicy.data;
        setExistingData(data);
        Object.keys(data).forEach(key => {
          setValue(key, data[key]);
        });
      } catch (error) {
        const SwalReact = withReactContent(Swal);
        SwalReact.fire({
          title: 'Erro!',
          text: error?.response?.data?.message ?? error.message,
          icon: 'error',
        });
      }
    }
  }, [id, idVersion, setValue]);

  useEffect(() => {
    getPolicyVersion();
  }, [getPolicyVersion]);

  return (
    <Container title="Políticas" breadcrumb="Adequações / Políticas / Versões" linkPage={`/policies/${id}/versions`}>
      <div className="card-content">
        {getTitleStep()}
        {!finished && (
          <VersionFormComponent
            control={control}
            errors={errors}
            getValues={getValues}
            handleBack={handleBack}
            handleComplete={handleComplete}
            handleUpdateAttachment={handleUpdateAttachment}
            handleSubmit={handleSubmit}
            id={id}
            isLoading={mutation.isLoading}
            register={register}
            setValue={setValue}
            existingData={existingData}
          />
        )}
        {mutation.isSuccess && finished && (
          <FormSuccess
            title="Versão da Política cadastrada com sucesso!"
            labelStartAgain="Ver Detalhes"
            handleStartAgain={() => navigate(`/policies/show/${id}/versions/${idPolicyVersion}`)}
            showButtonSeeDetail={false}
          />
        )}
      </div>
    </Container>
  );
};
